// src/analytics.js
// GA4 custom event helper — consent-aware, silent fail
import { getAnalytics, logEvent as firebaseLogEvent, setUserProperties } from "firebase/analytics";
import { localStore } from "./utils/safeStorage";

const COOKIE_KEY = "sendly-cookie-consent";

/**
 * Consent-aware logEvent wrapper.
 * Skips silently if analytics consent is not granted.
 * Never throws — safe to call from any component.
 * Returns true when the event was handed to Analytics, so a once-per-X caller
 * can keep its marker unset until the event really went out.
 */
export function logEvent(eventName, params = {}) {
  if (localStore.get(COOKIE_KEY) !== "accepted") return false;
  try {
    const analytics = getAnalytics();
    firebaseLogEvent(analytics, eventName, params);
    return true;
  } catch (e) {
    // Analytics not initialized, blocked by adblocker, or other error — silent
    return false;
  }
}

/**
 * Consent-aware setUserProperties wrapper.
 */
export function setAnalyticsUserProperties(properties) {
  if (localStore.get(COOKIE_KEY) !== "accepted") return;
  try {
    const analytics = getAnalytics();
    setUserProperties(analytics, properties);
  } catch (e) {
    // silent
  }
}

// ── Event name constants ──
// Usage: import { EVT } from "../analytics";  logEvent(EVT.ASCENT_TOGGLE, {...})
export const EVT = {
  // Auth & Onboarding
  SIGN_UP:                  "sign_up",
  // An INTERACTIVE sign-in only, logged from its UserCredential by
  // utils/signInAnalytics.js — never from a session restore. params: method
  // (google|apple|email_link|password)
  LOGIN:                    "login",
  GYM_JOIN:                 "gym_join",
  // No "consent_shown": the legal modal opens before the cookie banner is
  // usually answered, so the consent-gated logEvent dropped most of them.
  // Who never accepted is read from users/{uid} without legalConsent.
  CONSENT_ACCEPTED:         "consent_accepted",

  // Map & Route
  MAP_VIEW:                 "map_view",
  ROUTE_OPEN:               "route_open",
  // params: gym_id, action (log|repeat|undo|undo_toast|repeat_undo|attempt|attempt_undo|change_grade_vote|delete_try|delete_send|delete_repeat|delete_log|delete_day|set_attempts|set_flash),
  // is_flash (bool), attempts_bucket (1|2-3|4-9|10+ — on action log only, when the tries are known)
  ASCENT_TOGGLE:            "ascent_toggle",
  ROUTE_RATE:               "route_rate",
  ROUTE_COMMENT:            "route_comment",
  ROUTE_FILTER_USED:        "route_filter_used",

  // Spraywall
  SPRAY_WALL_VIEW:          "spray_wall_view",        // params: gym_id — a wall's view opened (once per opening)
  SPRAY_HOLDS_DETECT:       "spray_holds_detect",     // params: gym_id, result (done|failed), mode (fresh|refresh) — run doc turned done/failed
  // params: gym_id, action (create|new_version|publish_version|discard_draft|approve_holds|photo_refresh|edit_holds|delete|place_on_map|
  //   focus_view|restore_hold|resume_review|dismiss_detection|outline_edit|outline_auto|outline_clear|remove_outside)
  SPRAY_WALL_MANAGE:        "spray_wall_manage",
  SPRAY_HOLD_USAGE_VIEW:    "spray_hold_usage_view",
  SPRAY_PROBLEM_CREATE:     "spray_problem_create",   // params: gym_id, visibility (public|friends|private), has_tags (bool) — after a saveSprayProblem create
  SPRAY_PROBLEM_LOG:        "spray_problem_log",      // params: gym_id, status (sent|project), flash (bool), action (log|undo_toast|delete_log|delete_try|delete_send|delete_repeat|delete_day|set_attempts|set_flash|change_grade_vote) — after the log batch commit
  SPRAY_PROBLEM_RATE:       "spray_problem_rate",     // params: gym_id — after a rating create or update setDoc (not when the stars are taken back)
  SPRAY_PROBLEM_FILTER:     "spray_problem_filter",   // params: gym_id, action (apply|clear|search|attention) — list filter sheet apply, clear-all, first search character, an attention button turning its filter on
  SPRAY_COMMENT_CREATE:     "spray_comment_create",   // params: gym_id — after submitSprayComment
  SPRAY_REPORT_CREATE:      "spray_report_create",    // params: gym_id, reason (wrongProblem|offensive|other) — after submitSprayReport
  SPRAY_LIST_CREATE:        "spray_list_create",      // params: gym_id, circuit (bool), official (bool), visibility (private|friends|gym) — after the list doc create
  // params: gym_id, action (hide|unhide|benchmark_on|benchmark_off|delete_problem|delete_comment|resolve_report|edit_problem)
  SPRAY_MODERATION:         "spray_moderation",
  SPRAY_PROBLEM_DELETE:     "spray_problem_delete",   // params: gym_id — the author's own problem delete (no moderation cap)
  SPRAY_JOURNAL_OPEN:       "spray_journal_open",     // params: gym_id, source (stats_box|deep_link|profile) — the Statistics tab's spray-journal window opened
  SPRAY_STATS_VIEW:         "spray_stats_view",       // params: gym_id — the Statistics tab's Spraywall box shown open, once per session

  // Stats & Leaderboard
  LEADERBOARD_VIEW:         "leaderboard_view",
  PERSONAL_STATS_VIEW:      "personal_stats_view",
  FRIEND_COMPARISON_VIEW:   "friend_comparison_view",
  GLOBAL_PROFILE_VIEW:      "global_profile_view",
  PROFILE_SECTION_VIEW:     "profile_section_view",

  // Friends
  FRIEND_REQUEST_SENT:      "friend_request_sent",
  FRIEND_REQUEST_ACCEPTED:  "friend_request_accepted",

  // Competition
  COMPETITION_REGISTER:     "competition_register",
  COMPETITION_ATTEMPT:      "competition_attempt",
  COMPETITION_DASHBOARD_SHARE: "competition_dashboard_share",
  // Someone opened a competition session timeline. Params: status
  // (active|closed), surface (competition|pastCard|stats), subject
  // (self|friend|climber) — the last one is how we learn whether the coach
  // capability is worth keeping.
  COMPETITION_TIMELINE_VIEW: "competition_timeline_view",
  CHALLENGE_PARTICIPATION:  "challenge_participation",
  CHALLENGE_ROUTE_EXCLUSION: "challenge_route_exclusion",
  // Someone opened the collapsed long description. Tells a gym whether the
  // full announcement is actually read. Params: kind=competition|event,
  // surface=card|registration|landing|calendar. lang_fallback=true when the
  // viewer's language had no text and the gym's default-language text was
  // shown instead — whether translations get read at all.
  LONG_DESCRIPTION_EXPAND:  "long_description_expand",
  // Someone opened a collapsed card, or a category's expander. Params:
  // kind=competition|challenge|external|series|event|group,
  // surface=comp_tab|admin. Never fired on the public /register landing or
  // the widget — no consent flow runs there.
  CARD_EXPAND:              "card_expand",

  // Series
  SERIES_VIEW:              "series_view",

  // Events
  EVENT_REGISTER:           "event_register",
  EVENT_UNREGISTER:         "event_unregister",
  // Attendance recorded on an event day, after setEventAttendance succeeded.
  // Params: gym_id, mode=single|bulk, count (marks actually changed). The mode
  // is the question worth asking: if the sheet is only ever filled with "mark
  // everybody", the per-person tick is not what the check-in surface is for.
  EVENT_CHECK_IN:           "event_check_in",

  // Wrapped
  WRAPPED_VIEW:             "wrapped_view",

  // Admin
  ADMIN_SECTION_VIEW:       "admin_section_view",
  ADMIN_MEMBER_ACTION:      "admin_member_action",
  ADMIN_ROUTE_CREATE:       "admin_route_create",
  ADMIN_ROUTE_ARCHIVE:      "admin_route_archive",
  // Competition editor stubbing a route set in one go. Params: count,
  // and named=true when a prefix/suffix pattern was used instead of
  // plain 1..N — that is the whole question about this feature.
  ADMIN_ROUTE_BULK_CREATE:  "admin_route_bulk_create",
  MAP_ARCHIVE_MODE:         "map_archive_mode",
  ADMIN_MAP_UPLOAD:         "admin_map_upload",
  ADMIN_COMPETITION_CREATE: "admin_competition_create",
  // An organiser text (competition / event / series description, competition
  // rules) saved with a different set of filled languages than before.
  // Params: kind=competition|event|series, langs="hu"|"en"|"hu+en" (in
  // SUPPORTED_LANGS order). Tells whether the multilingual texts get used.
  ADMIN_TEXT_LANGS:         "admin_text_langs",
  ADMIN_COMPETITION_SCORING_CHANGE: "admin_competition_scoring_change",
  ADMIN_COMPETITION_REG_COUNTS_TOGGLE: "admin_competition_reg_counts_toggle",
  ADMIN_COMPETITION_IMPORT_EXCEL: "admin_competition_import_excel",
  ADMIN_COMPETITION_TEMPLATE_COPY: "admin_competition_template_copy",
  ADMIN_COMPETITION_TEMPLATE_PASTE: "admin_competition_template_paste",
  // Event templates (clipboard / .json). Copy params: gym_id, groups_count,
  // days_count, method=clipboard|download. Paste: gym_id, groups_count,
  // days_count, source=paste.
  ADMIN_EVENT_TEMPLATE_COPY: "admin_event_template_copy",
  ADMIN_EVENT_TEMPLATE_PASTE: "admin_event_template_paste",
  ADMIN_COMPETITION_GUEST_ADD: "admin_competition_guest_add",
  ADMIN_COMPETITION_PARTICIPANT_ADD: "admin_competition_participant_add",
  // Groups tab saved with a real change: `reordered` (the up/down order moved)
  // and `capped_groups` (how many groups carry a hardest-level cap).
  ADMIN_COMPETITION_GROUPS_CONFIG: "admin_competition_groups_config",
  // Organiser operations log opened. `scope` = competition | event, so one
  // event name covers both surfaces instead of two near-identical names.
  ADMIN_OPS_LOG_VIEW:       "admin_ops_log_view",
  ADMIN_SETTINGS_CHANGE:    "admin_settings_change",
  ADMIN_VAT_OVERRIDE_SET:   "admin_vat_override_set",
  ADMIN_BILLING_CURRENCY:   "admin_billing_currency",
  TV_SCREEN_APPROVED:       "tv_screen_approved",
  PLATFORM_USER_VIEW:       "platform_user_view",
  PLATFORM_USER_BAN:        "platform_user_ban", // param: action=ban|unban
  // Quarterly billing summary recipient list (PlatformBillingManagement).
  // params: action=add|update|remove, count=list size after the change. The
  // address itself is never a param.
  PLATFORM_BILLING_RECIPIENT: "platform_billing_recipient",
  // Spraywall hold-detection model switched on the platform page, fired after
  // the platform/config write succeeded (PlatformHoldDetector).
  PLATFORM_HOLD_MODEL_SWITCH: "platform_hold_model_switch", // params: model, action (activate|rollback)
  // Spraywall training consent granted / revoked for a gym, fired after the
  // setSprayTrainingConsent callable succeeded.
  PLATFORM_SPRAY_TRAINING_CONSENT: "platform_spray_training_consent", // params: gym_id, action (on|off) — PlatformGymEditor
  // Name moderation
  NAME_REVIEW_RESOLVED:     "name_review_resolved", // param: action=approved|rejected
  NAME_RESET:               "name_reset",           // param: mode=original|generated
  NAME_REPORTED:            "name_reported",         // gym-side report; param: gym_id

  // PWA & Platform
  // The install invitation (useInstallBanner). params: gym_id (gym page only),
  // action (shown — once per browser session | dismiss | prompt_accepted |
  // prompt_dismissed). Replaced pwa_install_prompt, which counted page loads.
  PWA_INSTALL_BANNER:       "pwa_install_banner",
  PWA_INSTALLED:            "pwa_installed",      // params: gym_id — appinstalled (no iOS equivalent: see the display_mode user property)
  // A forced update across a compatibility-level bump (useClientCompat). Sent
  // ONCE by the NEW build after a successful boot, from the marker the old
  // build left in sessionStorage; never on a public-dashboard / non-'app' path.
  // params: trigger (boot|session|callable), method (waiting_sw|hard),
  // level_from, level_to, gym_id — NEW custom dimensions: level_from, level_to
  CLIENT_FORCED_UPDATE:     "client_forced_update",
  // The stale-chunk recovery reloaded the page (a deploy pulled the user out of
  // a view) and the app booted again. Sent ONCE by the NEW page ~5 s after boot
  // from the sessionStorage record the boot script left before the reload
  // (utils/chunkRecoveryReport.js); never on a public-dashboard / non-'app'
  // path. params: recovery_path (light_reload|waiting_sw|hard|stalled_reload|
  // offline_reload), gym_id — NEW custom dimension: recovery_path
  CHUNK_RECOVERY:           "chunk_recovery",
  GYM_SWITCH:               "gym_switch",
  // Profile menu → the gym's own inbox. Says whether members actually route
  // gym-specific reports ("this route isn't on the map") to the gym instead
  // of to Sendly support. Param: gym_id.
  CONTACT_GYM:              "contact_gym",
  TAB_VIEW:                 "tab_view",
  THEME_CHANGE:             "theme_change",
  LANGUAGE_CHANGE:          "language_change",
  AVATAR_STYLE_CHANGE:      "avatar_style_change",
  // Sign-in identity management (profile → Bejelentkezés). `method` is the
  // provider id — low-cardinality, no PII.
  AUTH_METHOD_LINK:         "auth_method_link",
  AUTH_METHOD_UNLINK:       "auth_method_unlink",
  AUTH_EMAIL_CHANGE_START:  "auth_email_change_start",
  // A Google / Apple popup the Auth SDK settled as "closed" answered later
  // (weak network) — kind: sign_in/link/reauth, outcome: success/failure/unknown.
  AUTH_POPUP_LATE:          "auth_popup_late",

  // Export
  DATA_EXPORT:              "data_export",
  COMPETITION_EXPORT:       "competition_export",
  // Billing list Excel. ONE name for both modules — `scope` = competition |
  // event separates them, the way `admin_ops_log_view` does, and `surface`
  // says which of the four competition buttons they actually reach for.
  ADMIN_BILLING_EXPORT:     "admin_billing_export",
  // Staff override of one entry's early-bird tier, logged after the callable
  // succeeded. `module` = competition | event, `action` = grant | revoke | auto.
  ADMIN_EARLY_BIRD_OVERRIDE: "admin_early_bird_override",

  // Public gym widget
  PUBLIC_WIDGET_ENABLED:          "public_widget_enabled",
  PUBLIC_WIDGET_DISABLED:         "public_widget_disabled",
  PUBLIC_WIDGET_API_KEY_GENERATED: "public_widget_api_key_generated",

  // Sendly MCP server add-on (docs/spec/20-mcp-server.md).
  MCP_GRANT_CREATED: "mcp_grant_created", // params: scope_count, has_write — /oauth/authorize approve
  MCP_GRANT_REVOKED: "mcp_grant_revoked", // params: kind (oauth|pat), surface (settings|platform)
  MCP_PAT_CREATED:   "mcp_pat_created",   // params: scope_count, has_write — Settings PAT creation
  // Admin panel "AI-kapcsolat" section (AdminMcpConnection.jsx).
  MCP_SETUP_VIEW:    "mcp_setup_view",    // params: gym_id — section opened
  MCP_WRITE_TOGGLE:  "mcp_write_toggle",  // params: gym_id, action (on|off)
  // Kiegészítő (add-on) framework, PlatformGymEditor's "Kiegészítők" block —
  // docs/spec/15-platform-admin.md / 15b-plan-enforcement.md.
  ADMIN_ADDON_TOGGLE: "admin_addon_toggle", // params: gym_id, addon, action (on|off) — after a successful setGymAddon
  ADMIN_ADDON_DISCOUNT_SET: "admin_addon_discount_set", // params: gym_id, addon, action (set|clear) — after a successful billing.addonDiscounts write; the percentage is not a param

  // Help hints (docs/spec/21n-log-help-hints.md): a help ⓘ bubble was OPENED.
  HELP_TIP_OPEN:     "help_tip_open",     // params: gym_id, tip (log_buttons|opinion|chart|count|foot_rule) — NEW custom dimension: tip
};
